import {
  PlaceholderComponentProps,
  Text,
  getFieldValue,
  useSitecoreContext,
  withDatasourceCheck,
  // WithPlaceholderSpec,
  withPlaceholder,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import React, { useEffect } from 'react';
import { useRouter } from 'next/router';

import Link from 'components/helpers/Link/Link';
import { GetLinkFieldIcon } from 'models/fields';
import { TabProps } from 'components/Tabs/Tab/Tab';
import type { ComponentProps } from 'lib/component-props';
import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';

import styles from './TabList.module.scss';

type TabListProps = ComponentProps &
  PlaceholderComponentProps & {
    placeholders: React.ReactElement<TabProps>[];
  };

const cx = classNames.bind(styles);

const TabList = (props: TabListProps): JSX.Element => {
  const { className, placeholders } = props;

  const { sitecoreContext } = useSitecoreContext();
  const { pageEditing } = sitecoreContext;

  const defaultSelectedTabId = pageEditing ? '' : `tab-${placeholders[0]?.props.rendering?.uid}`;
  const [selectedTabId, setSelectedTabId] = React.useState(defaultSelectedTabId);
  const router = useRouter();

  //To handle hash URL navigation and tab activation
  useEffect(() => {
    const handleSelectedTabChange = (hash: string) => {
      const hashIndex = hash.indexOf('#tabpanel-');
      if (hashIndex !== -1) {
        const tabId = `tab-${hash.substring(hashIndex + '#tabpanel-'.length)}`;
        setSelectedTabId(tabId);
      }
    };

    if (router.isReady) {
      const hash = router.asPath;
      handleSelectedTabChange(hash);
    }

    const handleHashChange = () => {
      const hash = window.location.hash;
      handleSelectedTabChange(hash);
      window.scrollTo(0, 0);
    };
    window.addEventListener('hashchange', handleHashChange);
    // Clean up the event listeners when the component is unmounted
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [router.isReady, router.asPath]);

  const classes = cx('tabs', 'component-container-edge', className);
  if (pageEditing) {
    return <div className={classes}>{placeholders}</div>;
  }

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    const { id } = e.target as HTMLAnchorElement;
    setSelectedTabId(id);

    e.preventDefault();
    e.stopPropagation();
  };

  const tabs: JSX.Element[] = [];
  const tabpanels: JSX.Element[] = [];

  placeholders
    .sort((placeholderA, placeholderB) => {
      const fieldsA = placeholderA.props.fields;
      const fieldsB = placeholderB.props.fields;

      const orderValueA = getFieldValue<number>(fieldsA, 'order') || 1;
      const orderValueB = getFieldValue<number>(fieldsB, 'order') || 1;

      return orderValueA - orderValueB;
    })
    .forEach((placeholder) => {
      const { fields, rendering } = placeholder.props;
      const { name } = fields;
      const { uid } = rendering;

      const tabId = `tab-${uid}`;
      const selected = tabId === selectedTabId;
      // const nameValue = getFieldValue<string>(fields, 'name') || 'Tab';
      const hyperLink = fields.externalLink?.value.href ? (
        <Link
          field={fields.externalLink}
          aria-current={selected}
          className={cx('tab')}
          icon={GetLinkFieldIcon(fields.externalLink)}
          showLinkTextWithChildrenPresent={false}
        >
          <Text field={name} />
        </Link>
      ) : (
        <a
          aria-current={selected}
          className={cx('tab')}
          // data-name={nameValue}
          href={`#tabpanel-${uid}`}
          id={tabId}
          onClick={handleClick}
        >
          <Text field={name} />
        </a>
      );

      if (selected) {
        tabpanels.push(placeholder);
      }

      tabs.push(<li key={tabId}>{hyperLink}</li>);
    });

  return (
    <div className={classes}>
      <ul className={cx('tablist')}>{tabs}</ul>
      {tabpanels}
    </div>
  );
};

export default compose<TabListProps>(
  withDatasourceCheck(),
  withPlaceholder({
    placeholder: 'hp-tab',
    prop: 'placeholders',
  }),
  withStyles()
)(TabList);
